'use client'

import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useCookieConsent } from './CookieConsentProvider'
import styles from './CookieSettingsModal.module.scss'
import { CATEGORY_INFO, ConsentCategory, ConsentPreferences, CONSENT_CATEGORIES } from './types'

const DEFAULT_LOCAL_PREFS: ConsentPreferences = {
  necessary: true,
  functional: false,
  analytical: false,
  performance: false,
  marketing: false,
}

/**
 * Granular cookie settings modal.
 * Allows users to toggle individual consent categories.
 * Strictly Necessary is always ON and cannot be toggled.
 * WCAG 2.2 AA compliant: focus trap, keyboard navigation, ARIA labels.
 */
const CookieSettingsModal = () => {
  const { showSettings, preferences, acceptAll, savePreferences, closeSettings } = useCookieConsent()

  if (!showSettings) return null

  return (
    <CookieSettingsModalContent
      preferences={preferences}
      acceptAll={acceptAll}
      savePreferences={savePreferences}
      closeSettings={closeSettings}
    />
  )
}

interface CookieSettingsModalContentProps {
  preferences: ConsentPreferences | null
  acceptAll: () => void
  savePreferences: (preferences: ConsentPreferences) => void
  closeSettings: () => void
}

const CookieSettingsModalContent = ({
  preferences,
  acceptAll,
  savePreferences,
  closeSettings,
}: CookieSettingsModalContentProps) => {
  const [localPrefs, setLocalPrefs] = useState<ConsentPreferences>(() => ({
    ...(preferences ?? DEFAULT_LOCAL_PREFS),
    necessary: true,
  }))

  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement
  }, [])

  // Focus trap + keyboard handling
  useEffect(() => {
    const modal = modalRef.current

    if (!modal) return

    // Focus the modal on open
    modal.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSettings()

        return
      }

      if (e.key === 'Tab') {
        const focusable = modal.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault()
            last?.focus()
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault()
            first?.focus()
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    // Prevent body scroll
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = originalOverflow

      // Restore focus
      previousFocusRef.current?.focus()
    }
  }, [closeSettings])

  const handleToggle = useCallback((category: ConsentCategory) => {
    if (category === 'necessary') return // Cannot toggle necessary
    setLocalPrefs((prev) => ({ ...prev, [category]: !prev[category] }))
  }, [])

  const handleSave = useCallback(() => {
    savePreferences(localPrefs)
  }, [savePreferences, localPrefs])

  return (
    <div className={styles.overlay} onClick={closeSettings} aria-hidden='true'>
      <div
        ref={modalRef}
        className={styles.modal}
        role='dialog'
        aria-modal='true'
        aria-label='Cookie preferences'
        aria-describedby='cookie-settings-description'
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>Cookie Preferences</h2>
          <button
            type='button'
            className={styles.closeButton}
            onClick={closeSettings}
            aria-label='Close cookie settings'
          >
            &#10005;
          </button>
        </div>

        <p id='cookie-settings-description' className={styles.description}>
          Manage your cookie preferences below. Strictly necessary cookies cannot be disabled as they are
          essential for the website to function.{' '}
          <Link href='/cookie-policy' className={styles.policyLink} target='_blank' rel='noopener noreferrer' prefetch={false}>
            View full Cookie Policy
          </Link>
        </p>

        <div className={styles.categories}>
          {CONSENT_CATEGORIES.map((category) => {
            const info = CATEGORY_INFO[category]
            const isNecessary = category === 'necessary'
            const isEnabled = localPrefs[category]

            return (
              <div key={category} className={styles.category}>
                <div className={styles.categoryHeader}>
                  <label className={styles.categoryLabel} htmlFor={`cookie-toggle-${category}`}>
                    <span className={styles.categoryName}>{info.label}</span>
                  </label>
                  <div className={styles.toggleWrapper}>
                    {isNecessary && <span className={styles.alwaysOn}>Always Active</span>}
                    <button
                      id={`cookie-toggle-${category}`}
                      type='button'
                      role='switch'
                      aria-checked={isEnabled}
                      aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${info.label} cookies`}
                      className={`${styles.toggle} ${isEnabled ? styles.toggleOn : styles.toggleOff} ${isNecessary ? styles.toggleDisabled : ''}`}
                      disabled={isNecessary}
                      onClick={() => handleToggle(category)}
                    >
                      <span className={styles.toggleThumb} />
                    </button>
                  </div>
                </div>
                <p className={styles.categoryDescription}>{info.description}</p>
              </div>
            )
          })}
        </div>

        <div className={styles.footer}>
          <button
            type='button'
            className={styles.saveButton}
            onClick={handleSave}
            aria-label='Save cookie preferences'
          >
            Save Preferences
          </button>
          <button
            type='button'
            className={styles.acceptAllButton}
            onClick={acceptAll}
            aria-label='Accept all cookies'
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  )
}

export default CookieSettingsModal
